import React, { useState, useEffect, useMemo } from 'react';
import { Lock } from 'lucide-react';
import { useSaisieMatricielleMutation, useGetNotesExamensQuery } from '../../services/notesExamensApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useGetParametreByKeyQuery } from '../../services/coreApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { typeFormationForClasse } from '../../utils/typeFormation';

// Helper : détecte si un paramètre "verrou" est actif (true/1/oui/on).
const isVerrouActif = (p: any): boolean => {
  if (!p) return false;
  const v = (p.valeur_convertie ?? p.valeur ?? '').toString().toLowerCase().trim();
  return v === 'true' || v === '1' || v === 'oui' || v === 'on';
};

interface NoteExamenMatrice {
  id: number;
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  note_examen: number | null;
  absent: boolean;
  verrouille: boolean;
}

interface SaisieMatricielleExamensProps {
  classe: string;
  matiereId: number;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  estRattrapage: boolean;
  onSuccess?: () => void;
}

const SaisieMatricielleExamens: React.FC<SaisieMatricielleExamensProps> = ({
  classe,
  matiereId,
  anneeAcademique,
  semestre,
  estRattrapage,
  onSuccess,
}) => {
  // CS si la classe finit par "-CS", sinon FI + DD.
  const { data: etudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({ classe, type_formation: typeFormationForClasse(classe) });

  // Récupérer les notes existantes
  const { data: notesExistantes, isLoading: loadingNotes, refetch: refetchNotes } = useGetNotesExamensQuery({
    matiere_id: matiereId,
    annee_academique: anneeAcademique,
    semestre: semestre,
    est_rattrapage: estRattrapage,
  });

  const [saisieMatricielle, { isLoading: isSaving }] = useSaisieMatricielleMutation();

  const [notes, setNotes] = useState<NoteExamenMatrice[]>([]);
  const [modified, setModified] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fix 2026-08-28 : détection semestre clôturé (parité + session ord/ratt)
  // Miroir de ce qui existe déjà dans SaisieMatricielle (devoirs) — les agents
  // avaient été surpris que la saisie examens reste ouverte alors que les
  // devoirs étaient verrouillés pour le même semestre.
  const { data: pVerrouGlobal } = useGetParametreByKeyQuery('saisie_notes_verrouillee');
  const { data: pVerrouS1 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1');
  const { data: pVerrouS2 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2');
  const { data: pVerrouS1Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ord');
  const { data: pVerrouS1Ratt } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ratt');
  const { data: pVerrouS2Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ord');
  const { data: pVerrouS2Ratt } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ratt');

  // Fix 2026-10-15 : ces verrous (config partagé) ne concernent que l'année
  // ACTIVE. Pour une année antérieure, aucun verrou opérationnel ne s'applique.
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const estAnneeActive = !anneeActive || anneeAcademique === anneeActive.libelle;
  const semestreCloture = useMemo(() => {
    if (!estAnneeActive) return null;
    if (isVerrouActif(pVerrouGlobal)) return { motif: 'global', label: 'Saisie fermée globalement' };
    if (semestre === 'S1') {
      if (isVerrouActif(pVerrouS1)) return { motif: 's1', label: 'Semestre impair (S1) clôturé' };
      if (estRattrapage && isVerrouActif(pVerrouS1Ratt))
        return { motif: 's1_ratt', label: 'Session rattrapage S1 clôturée' };
      if (!estRattrapage && isVerrouActif(pVerrouS1Ord))
        return { motif: 's1_ord', label: 'Session ordinaire S1 clôturée' };
    }
    if (semestre === 'S2') {
      if (isVerrouActif(pVerrouS2)) return { motif: 's2', label: 'Semestre pair (S2) clôturé' };
      if (estRattrapage && isVerrouActif(pVerrouS2Ratt))
        return { motif: 's2_ratt', label: 'Session rattrapage S2 clôturée' };
      if (!estRattrapage && isVerrouActif(pVerrouS2Ord))
        return { motif: 's2_ord', label: 'Session ordinaire S2 clôturée' };
    }
    return null;
  }, [pVerrouGlobal, pVerrouS1, pVerrouS2, pVerrouS1Ord, pVerrouS1Ratt, pVerrouS2Ord, pVerrouS2Ratt, semestre, estRattrapage, estAnneeActive, anneeAcademique]);

  const isLoading = loadingEtudiants || loadingNotes;

  // Initialiser les données quand les étudiants et notes sont chargés
  useEffect(() => {
    if (etudiants && etudiants.length > 0) {
      const initialNotes: NoteExamenMatrice[] = etudiants.map((etudiant) => {
        // Chercher si une note existe déjà pour cet étudiant
        const noteExistante = notesExistantes?.find(
          (n: any) => n.etudiant === etudiant.id || n.etudiant_matricule === etudiant.matricule
        );

        return {
          id: noteExistante?.id || 0,
          etudiant_id: etudiant.id,
          matricule: etudiant.matricule,
          nom: etudiant.nom,
          prenoms: etudiant.prenoms,
          note_examen: noteExistante?.note_examen ? parseFloat(String(noteExistante.note_examen)) : null,
          absent: noteExistante?.absent || false,
          verrouille: noteExistante?.verrouille || false,
        };
      });
      setNotes(initialNotes);
      setModified(new Set());
    }
  }, [etudiants, notesExistantes]);

  // Mettre à jour une note (refuse silencieusement les valeurs hors plage)
  const handleNoteChange = (etudiantId: number, field: string, value: any) => {
    // Fix 2026-08-28 : blocage si semestre clôturé
    if (semestreCloture) {
      setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — saisie refusée` });
      return;
    }
    if (field === 'note_examen') {
      const numValue = value === '' ? null : parseFloat(value);
      if (numValue !== null) {
        if (Number.isNaN(numValue) || numValue < 0 || numValue > 20) {
          // Note invalide : refuser et alerter
          setMessage({ type: 'error', text: 'La note doit être comprise entre 0 et 20.' });
          return;
        }
      }
      value = numValue;
    }

    setNotes(prev => prev.map(note => {
      if (note.etudiant_id === etudiantId) {
        const updatedNote = { ...note, [field]: value };

        // Si on coche absent, mettre la note à 0
        if (field === 'absent' && value === true) {
          updatedNote.note_examen = 0;
        }

        return updatedNote;
      }
      return note;
    }));

    setModified(prev => new Set(prev).add(etudiantId));
  };

  // Enregistrer les modifications
  const handleSave = async () => {
    setMessage(null);

    // Fix 2026-08-28 : blocage définitif si semestre clôturé
    if (semestreCloture) {
      setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — enregistrement refusé` });
      return;
    }

    const notesToUpdate = notes
      .filter(note => modified.has(note.etudiant_id))
      .map(note => ({
        etudiant_id: note.etudiant_id,
        note_examen: note.note_examen,
        absent: note.absent,
      }));

    if (notesToUpdate.length === 0) {
      setMessage({ type: 'error', text: 'Aucune modification à enregistrer' });
      return;
    }

    try {
      const result = await saisieMatricielle({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        est_rattrapage: estRattrapage,
        notes: notesToUpdate,
      }).unwrap();

      if (result.erreurs && result.erreurs > 0) {
        setMessage({
          type: 'error',
          text: `${result.crees + result.modifies} notes enregistrées, ${result.erreurs} erreurs`,
        });
      } else {
        setMessage({
          type: 'success',
          text: `${result.crees + result.modifies} notes enregistrées avec succès !`,
        });
        setModified(new Set());
        refetchNotes();
        onSuccess?.();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.data?.error || error.data?.message || 'Erreur lors de l\'enregistrement des notes',
      });
    }
  };

  // Obtenir le statut de la note
  const getNoteStatus = (note: number | null, absent: boolean) => {
    if (absent) return { label: 'Absent', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' };
    if (note === null) return { label: 'Non saisi', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' };
    if (note < 5) return { label: 'Éliminatoire', color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' };
    if (note >= 10) return { label: 'Validé', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' };
    return { label: 'À valider', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de la matrice des notes...</p>
        </div>
      </div>
    );
  }

  if (!etudiants || etudiants.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
        <p className="text-yellow-700 dark:text-yellow-300">Aucun étudiant trouvé pour la classe {classe}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header condensé */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Saisie Notes Examens - Tableau Matriciel</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Classe: {classe} | {notes.length} étudiants | Semestre: {semestre} | Session: {estRattrapage ? 'Rattrapage' : 'Ordinaire'}
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={modified.size === 0 || isSaving || !!semestreCloture}
          title={semestreCloture ? `🔒 ${semestreCloture.label} — enregistrement refusé` : ''}
          className={`text-white px-6 py-2 rounded-lg text-sm font-semibold ${
            semestreCloture
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          {semestreCloture ? '🔒 Verrouillé' : (isSaving ? 'Enregistrement...' : `Enregistrer (${modified.size} modifié${modified.size > 1 ? 's' : ''})`)}
        </button>
      </div>

      {/* Bandeau SEMESTRE CLÔTURÉ (fix 2026-08-28) — miroir de la saisie devoirs */}
      {semestreCloture && (
        <div className="rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-center gap-3">
          <Lock size={22} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-red-800 dark:text-red-200 text-sm uppercase tracking-wide">
              🔒 Saisie verrouillée — {semestreCloture.label}
            </div>
            <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Vos modifications seront <strong>refusées</strong> à l'enregistrement.
              Contactez le super-administrateur pour rouvrir la saisie.
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Matricule
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Prénoms
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Note Examen
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Absent
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {notes.map((note) => {
                const status = getNoteStatus(note.note_examen, note.absent);
                return (
                  <tr key={note.etudiant_id} className={modified.has(note.etudiant_id) ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {note.matricule}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {note.nom}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {note.prenoms}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        max="20"
                        value={note.note_examen !== null ? note.note_examen : ''}
                        onChange={(e) => handleNoteChange(note.etudiant_id, 'note_examen', e.target.value)}
                        disabled={note.absent || note.verrouille || !!semestreCloture}
                        title={semestreCloture ? `🔒 ${semestreCloture.label} — saisie refusée` : ''}
                        className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={note.absent}
                        onChange={(e) => handleNoteChange(note.etudiant_id, 'absent', e.target.checked)}
                        disabled={note.verrouille || !!semestreCloture}
                        title={semestreCloture ? `🔒 ${semestreCloture.label} — saisie refusée` : ''}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                      />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {note.verrouille ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full">
                          Verrouillé
                        </span>
                      ) : modified.has(note.etudiant_id) ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
                          Modifié
                        </span>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SaisieMatricielleExamens;
